import { useState } from "react";
import { useMutation } from "@apollo/client";
import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import SectionHeading from "@/components/SectionHeading";
import Table from "@/components/Table";
import { renderEmptyCellState } from "@/components/Table/utils";
import { toFormat } from "@/lib/dateHelpers";
import { showSnackbar } from "@/lib/snackbarUtils";
import { useErrorBanner } from "@/providers/ErrorBannerProvider";
import { UPDATE_SUBMITTED_APPLICATION_MUTATION } from "../mutations";
import { MEMBERSHIP_APPLICATION_QUERY } from "../queries";
import type { ColumnDef } from "@tanstack/react-table";
import { canEditApplicationFields, hasEligibility } from "../utils";
import type { ApplicationDocument, EligibilityCheck } from "../queries";

function buildColumns(): ColumnDef<ApplicationDocument, unknown>[] {
  return [
    {
      id: "documentName",
      header: "Document Name",
      meta: { headerClassName: "hide-header-in-column" },
      accessorFn: (row) => row.filename,
      cell: ({ row }) => (
        <a
          href={row.original.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sys-brand-primary hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {row.original.filename}
        </a>
      ),
      size: 300
    },
    {
      id: "uploadDate",
      header: "Upload Date",
      accessorFn: (row) => row.createdAt,
      cell: ({ row }) => renderEmptyCellState(toFormat(row.original.createdAt, "MMM dd, yyyy")),
      size: 160
    }
  ];
}

const columns = buildColumns();

interface Props {
  applicationId: string;
  status: string;
  documents: ApplicationDocument[] | null;
  eligibilityStatuses: EligibilityCheck[];
}

export default function CriminalHistoryDocumentsSection({
  applicationId,
  status,
  documents,
  eligibilityStatuses
}: Props) {
  const rows = documents ?? [];
  const [dialogOpen, setDialogOpen] = useState(false);
  const { setError, clearError } = useErrorBanner();
  const canEdit = canEditApplicationFields({ status });
  const isCriminalHistoryEligible = hasEligibility(eligibilityStatuses, "CRIMINAL_HISTORY", "ELIGIBLE");

  const [updateApplication, { loading }] = useMutation(UPDATE_SUBMITTED_APPLICATION_MUTATION, {
    refetchQueries: [{ query: MEMBERSHIP_APPLICATION_QUERY, variables: { id: applicationId } }],
    onCompleted: (data) => {
      if (data.updateSubmittedApplication.success) {
        clearError();
        showSnackbar(data.updateSubmittedApplication.message);
        setDialogOpen(false);
      } else {
        setError(data.updateSubmittedApplication.message);
      }
    },
    onError: (error) => {
      setError(error.message);
    }
  });

  const handleToggle = () => {
    updateApplication({
      variables: {
        input: {
          id: applicationId,
          eligibility: {
            type: "CRIMINAL_HISTORY",
            status: isCriminalHistoryEligible ? "INELIGIBLE" : "ELIGIBLE"
          }
        }
      }
    });
  };

  return (
    <>
      <div className="flex flex-row justify-between w-full items-center">
        <SectionHeading
          heading="Criminal History Documents"
          body="View criminal history documents uploaded by the applicant below"
        />
        <div className="pt-8">
          {canEdit &&
            (isCriminalHistoryEligible ? (
              <Button variant="outlined" icon="undo" onClick={() => setDialogOpen(true)}>
                Unconfirm Eligibility
              </Button>
            ) : (
              <Button variant="outlined" icon="task" onClick={() => setDialogOpen(true)}>
                Confirm Eligibility
              </Button>
            ))}
        </div>
      </div>
      <Table<ApplicationDocument> loading={false} data={rows} columns={columns} getRowId={(row) => row.id} />

      <Dialog
        open={dialogOpen}
        onClosed={() => setDialogOpen(false)}
        headline={isCriminalHistoryEligible ? "Unconfirm Eligibility" : "Confirm Eligibility"}
        actions={
          <div className="flex gap-2">
            <Button variant="text" onClick={() => setDialogOpen(false)} disabled={loading}>
              Cancel
            </Button>
            <Button variant="filled" onClick={handleToggle} disabled={loading}>
              {isCriminalHistoryEligible ? "Unconfirm Eligibility" : "Confirm Eligibility"}
            </Button>
          </div>
        }
      >
        <p className="text-body-medium text-sys-brand-on-surface-variant">
          {isCriminalHistoryEligible
            ? "Are you sure you want to unconfirm this applicant's criminal history review? This will mark the verification step as incomplete."
            : "Are you sure you want to confirm this applicant's criminal history review? Confirmation will mark the application verification step as completed. Once all steps have been completed, the application can be approved."}
        </p>
      </Dialog>
    </>
  );
}
